import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Paper,
  Title,
  Group,
  Button,
  Text,
  Stack,
  Grid,
  Card,
  Badge,
  SimpleGrid,
  Progress,
  Alert,
  Timeline,
  ThemeIcon,
  ActionIcon,
  RingProgress,
  Avatar,
  Select,
  Table,
  Menu,
  rem,
} from '@mantine/core';
import {
  IconUsers,
  IconFileText,
  IconMail,
  IconClock,
  IconChevronRight,
  IconRefresh,
  IconCheck,
  IconHeart,
  IconBook,
  IconPlus,
  IconUserCheck,
  IconAlertCircle,
  IconDots,
  IconEye,
  IconEdit,
  IconActivity,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { 
  useTeacherProfile, 
  useTeacherClasses, 
  useTeacherMessages, 
  useDailyReports,
  useActivityVerifications 
} from '../../hooks/useTeacher';
import { useClassStudents as useClassStudentsNew } from '../../hooks/useClasses';
import { SmartLoader } from '../../components/common/LoadingStates';
import NotificationCenter from '../../components/common/NotificationCenter';

const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);

  const {
    data: teacher,
    // isLoading: teacherLoading,
    // error: teacherError,
  } = useTeacherProfile();

  const {
    data: classesData,
    // isLoading: classesLoading,
    // error: classesError,
  } = useTeacherClasses();

  const {
    data: messagesData,
    // isLoading: messagesLoading,
  } = useTeacherMessages({ status: 'unread' });

  const {
    data: dailyReportsData,
    // isLoading: reportsLoading,
  } = useDailyReports(new Date().toISOString().split('T')[0]);

  const {
    data: verificationsData,
    isLoading: verificationsLoading,
  } = useActivityVerifications();

  // Extract arrays from API responses
  const classes = Array.isArray(classesData) ? classesData : (classesData as any)?.classes || [];
  const messages = Array.isArray(messagesData) ? messagesData : (messagesData as any)?.messages || [];
  const dailyReports = Array.isArray(dailyReportsData) ? dailyReportsData : (dailyReportsData as any)?.daily_reports || [];
  const verifications = Array.isArray(verificationsData) ? verificationsData : (verificationsData as any)?.verifications || [];

  const selectedClass = classes?.find((cls: any) => cls.id === selectedClassId) || classes?.[0];
  
  // Use new hook for class students if school ID is available
  const { data: classStudentsData, isLoading: studentsLoading } = useClassStudentsNew(
    teacher?.school_id || '',
    selectedClass?.id || '',
    { per_page: 50 }
  );
  const classStudents = classStudentsData?.students || [];

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getToday = () => {
    return new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getTodayStats = () => {
    if (!selectedClass || !classStudents.length) {
      return { present: 0, absent: 0, late: 0, total: 0 };
    }
    
    const present = classStudents.filter((s: any) => s.attendance_status === 'present').length;
    const absent = classStudents.filter((s: any) => s.attendance_status === 'absent').length;
    const late = classStudents.filter((s: any) => s.attendance_status === 'late').length;
    
    return {
      present,
      absent,
      late,
      total: classStudents.length,
    };
  };

  const getReportStats = () => {
    if (!dailyReports) return { completed: 0, draft: 0, pending: 0, total: 0 };
    
    return {
      completed: dailyReports.filter((r: any) => r.status === 'completed' || r.status === 'sent').length,
      draft: dailyReports.filter((r: any) => r.status === 'draft').length,
      pending: dailyReports.filter((r: any) => r.status === 'pending').length,
      total: dailyReports.length
    };
  };

  const handleRefresh = () => {
    notifications.show({
      title: 'Dashboard Refreshed',
      message: 'Latest information has been loaded',
      color: 'green',
    });
  };

  const attendanceStats = getTodayStats();
  const reportStats = getReportStats();

  const attendancePercentage = attendanceStats.total > 0 
    ? Math.round((attendanceStats.present / attendanceStats.total) * 100)
    : 0;

  return (
    <Stack gap="lg">
      {/* Welcome Header */}
      <Card withBorder radius="lg" p="xl" style={{ background: 'linear-gradient(135deg, #44a08d 0%, #093637 100%)' }}>
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1} fw={700} c="white">
              {getTimeBasedGreeting()}, {teacher?.first_name || 'Teacher'}!
            </Title>
            <Text c="rgba(255,255,255,0.9)" size="lg" mt="xs">
              {getToday()} • Ready to inspire young minds today?
            </Text>
          </div>
          <Group gap="md">
            <NotificationCenter buttonProps={{ size: 'lg', variant: 'white' }} />
            <Badge size="lg" variant="white" color="dark">
              Teacher Portal
            </Badge>
            <ThemeIcon size="xl" radius="xl" variant="white" color="dark">
              <IconBook size={24} />
            </ThemeIcon>
          </Group>
        </Group>
      </Card>

      {/* Class Selector */}
      {classes.length > 0 && (
        <Card withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>Current Class</Title>
            <Select
              value={selectedClassId || classes[0]?.id}
              onChange={(value) => setSelectedClassId(value)}
              data={classes.filter((cls: any) => cls && cls.name).map((cls: any) => ({ 
                value: String(cls.id), 
                label: `${cls.name} (${cls.student_count || 0} students)` 
              }))}
              placeholder="Select a class"
              style={{ width: 300 }}
            />
          </Group>
          {selectedClass && (
            <Group gap="md">
              <Text size="sm">
                <Text span fw={600}>Teacher:</Text> {selectedClass.teacher?.full_name || 'You'}
              </Text>
              <Text size="sm">
                <Text span fw={600}>Age Range:</Text> {selectedClass.age_range}
              </Text>
              <Text size="sm">
                <Text span fw={600}>Status:</Text>{' '}
                <Badge size="sm" color={selectedClass.status === 'active' ? 'green' : 'gray'}>
                  {selectedClass.status}
                </Badge>
              </Text>
              <Button
                size="xs"
                variant="light"
                rightSection={<IconChevronRight size={14} />}
                onClick={() => navigate(`/teacher/classes/${selectedClass.id}`)}
              >
                Manage Class
              </Button>
            </Group>
          )}
        </Card>
      )}

      {/* Quick Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                My Classes
              </Text>
              <Text fw={700} size="xl">
                {classes?.length || 0}
              </Text>
            </div>
            <ThemeIcon color="blue" variant="light" size="xl">
              <IconUsers size={24} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                Total Students
              </Text>
              <Text fw={700} size="xl">
                {classes?.reduce((sum: number, cls: any) => sum + (cls.student_count || 0), 0) || 0}
              </Text>
            </div>
            <ThemeIcon color="green" variant="light" size="xl">
              <IconHeart size={24} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                Pending Tasks
              </Text>
              <Text fw={700} size="xl">
                {verifications?.length || 0}
              </Text>
            </div>
            <ThemeIcon color="orange" variant="light" size="xl">
              <IconAlertCircle size={24} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                New Messages
              </Text>
              <Text fw={700} size="xl">
                {messages?.length || 0}
              </Text>
            </div>
            <ThemeIcon color="violet" variant="light" size="xl">
              <IconMail size={24} />
            </ThemeIcon>
          </Group>
        </Card>
      </SimpleGrid>

      <Grid>
        {/* Attendance Overview */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card withBorder>
            <Group justify="space-between" mb="md">
              <Title order={4}>Today's Attendance - {selectedClass?.name || 'All Classes'}</Title>
              <Group gap="xs">
                <ActionIcon variant="subtle" onClick={handleRefresh}>
                  <IconRefresh size={16} />
                </ActionIcon>
                <Button 
                  size="xs" 
                  rightSection={<IconChevronRight size={14} />}
                  onClick={() => navigate('/teacher/attendance')}
                >
                  Mark Attendance
                </Button>
              </Group>
            </Group>

            <Grid mb="md">
              <Grid.Col span={4}>
                <Paper p="md" radius="md" withBorder>
                  <Group justify="center" mb="md">
                    <RingProgress
                      size={100}
                      thickness={8}
                      sections={[
                        { value: attendancePercentage, color: 'green' },
                      ]}
                      label={
                        <Text ta="center" fw={700} size="lg">
                          {attendancePercentage}%
                        </Text>
                      }
                    />
                  </Group>
                  <Text ta="center" size="sm" fw={500}>Attendance Rate</Text>
                </Paper>
              </Grid.Col>
              
              <Grid.Col span={8}>
                <SimpleGrid cols={3} spacing="sm">
                  <Paper p="md" radius="md" withBorder>
                    <Group gap="xs">
                      <ThemeIcon size="sm" color="green" variant="light">
                        <IconUserCheck size={14} />
                      </ThemeIcon>
                      <div>
                        <Text size="xs" c="dimmed">Present</Text>
                        <Text fw={600}>{attendanceStats.present}</Text>
                      </div>
                    </Group>
                  </Paper>
                  
                  <Paper p="md" radius="md" withBorder>
                    <Group gap="xs">
                      <ThemeIcon size="sm" color="orange" variant="light">
                        <IconClock size={14} />
                      </ThemeIcon>
                      <div>
                        <Text size="xs" c="dimmed">Late</Text>
                        <Text fw={600}>{attendanceStats.late}</Text>
                      </div>
                    </Group>
                  </Paper>
                  
                  <Paper p="md" radius="md" withBorder>
                    <Group gap="xs">
                      <ThemeIcon size="sm" color="red" variant="light">
                        <IconAlertCircle size={14} />
                      </ThemeIcon>
                      <div>
                        <Text size="xs" c="dimmed">Absent</Text>
                        <Text fw={600}>{attendanceStats.absent}</Text>
                      </div>
                    </Group>
                  </Paper>
                </SimpleGrid>
              </Grid.Col>
            </Grid>

            {/* Student List */}
            <Text fw={600} mb="sm">Students in {selectedClass?.name || 'Class'}</Text>
            <SmartLoader
              isLoading={studentsLoading}
              isEmpty={classStudents.length === 0}
              emptyState={
                <Paper p="xl" radius="md" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                  <Stack align="center" gap="md">
                    <ThemeIcon size="xl" variant="light" color="gray">
                      <IconUsers size={24} />
                    </ThemeIcon>
                    <Text fw={500}>No students in this class</Text>
                    <Button
                      size="sm"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => navigate(`/teacher/classes/${selectedClass?.id}/add-students`)}
                    >
                      Add Students
                    </Button>
                  </Stack>
                </Paper>
              }
            >
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Student</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Activities Today</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {classStudents.slice(0, 5).map((student: any) => (
                    <Table.Tr key={student.id}>
                      <Table.Td>
                        <Group gap="sm">
                          <Avatar size="sm" radius="xl">
                            {student.first_name[0]}{student.last_name[0]}
                          </Avatar>
                          <Text size="sm">{student.first_name} {student.last_name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          color={
                            student.attendance_status === 'present' ? 'green' :
                            student.attendance_status === 'late' ? 'orange' :
                            student.attendance_status === 'absent' ? 'red' : 'gray'
                          }
                        >
                          {student.attendance_status || 'Not Marked'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{student.activities_count || 0} activities</Text>
                      </Table.Td>
                      <Table.Td>
                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <ActionIcon variant="subtle" size="sm">
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEye style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => navigate(`/teacher/students/${student.id}`)}
                            >
                              View Profile
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                              onClick={() => navigate(`/teacher/students/${student.id}/report`)}
                            >
                              Write Report
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              
              {classStudents.length > 5 && (
                <Group justify="center" mt="md">
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => navigate(`/teacher/classes/${selectedClass?.id}/students`)}
                  >
                    View all {classStudents.length} students
                  </Button>
                </Group>
              )}
            </SmartLoader>
          </Card>
        </Grid.Col>

        {/* Quick Actions & Activity Feed */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            {/* Quick Actions */}
            <Card withBorder>
              <Title order={5} mb="md">Quick Actions</Title>
              <Stack gap="xs">
                <Button
                  variant="light"
                  leftSection={<IconUserCheck size={16} />}
                  fullWidth
                  onClick={() => navigate('/teacher/attendance')}
                >
                  Mark Attendance
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconFileText size={16} />}
                  fullWidth
                  onClick={() => navigate('/teacher/daily-reports')}
                >
                  Write Daily Reports
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconMail size={16} />}
                  fullWidth
                  onClick={() => navigate('/teacher/messages')}
                >
                  View Messages ({messages?.length || 0})
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconCheck size={16} />}
                  fullWidth
                  onClick={() => navigate('/teacher/activities/verify')}
                >
                  Verify Activities ({verifications?.length || 0})
                </Button>
              </Stack>
            </Card>

            {/* Daily Reports Status */}
            <Card withBorder>
              <Title order={5} mb="md">Daily Reports</Title>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm">Completed</Text>
                  <Badge color="green">{reportStats.completed}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">In Draft</Text>
                  <Badge color="yellow">{reportStats.draft}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Pending</Text>
                  <Badge color="gray">{reportStats.pending}</Badge>
                </Group>
                <Progress
                  value={reportStats.total > 0 ? (reportStats.completed / reportStats.total) * 100 : 0}
                  color="green"
                  size="sm"
                  radius="xl"
                  mt="xs"
                />
              </Stack>
            </Card>

            {/* Recent Activities */}
            <Card withBorder>
              <Group justify="space-between" mb="md">
                <Title order={5}>Recent Activities</Title>
                <Badge size="sm" variant="light">Live</Badge>
              </Group>
              
              <SmartLoader
                isLoading={verificationsLoading}
                isEmpty={verifications.length === 0}
                emptyState={
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    No activities to verify
                  </Text>
                }
              >
                <Timeline active={-1} bulletSize={20} lineWidth={2}>
                  {verifications.slice(0, 3).map((activity: any) => (
                    <Timeline.Item
                      key={activity.id}
                      bullet={<IconActivity size={12} />}
                      title={
                        <Text size="sm" fw={500}>
                          {activity.student?.first_name} {activity.student?.last_name}
                        </Text>
                      }
                    >
                      <Text size="xs" c="dimmed">
                        {activity.activity_type} • {formatDistanceToNow(new Date(activity.detected_at), { addSuffix: true })}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </SmartLoader>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Messages Alert */}
      {messages.length > 0 && (
        <Alert
          icon={<IconMail size={16} />}
          title={`You have ${messages.length} unread message${messages.length > 1 ? 's' : ''}`}
          color="blue"
          withCloseButton
        >
          <Group justify="space-between">
            <Text size="sm">
              Check your messages from parents and staff
            </Text>
            <Button size="xs" variant="light" onClick={() => navigate('/teacher/messages')}>
              View Messages
            </Button>
          </Group>
        </Alert>
      )}
    </Stack>
  );
};

export default TeacherDashboard;